import { useState, useMemo } from 'react';
import { TrendingUp, TrendingDown, Search, Star, Activity, BarChart3, Globe, Zap } from 'lucide-react';
import { useLiveMarketData } from '../../hooks/useLiveMarketData';
import CurrencyIcon from '../../components/CurrencyIcon';
import './MarketPage.css';

const MarketPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('marketCap');
  const [timeframe, setTimeframe] = useState('24h');
  const [favorites, setFavorites] = useState(() => {
    // Load favorites from localStorage
    const saved = localStorage.getItem('marketFavorites');
    return saved ? JSON.parse(saved) : [];
  });

  // Use live market data with WebSocket
  const { 
    coins, 
    trendingCoins, 
    globalStats, 
    isLoading, 
    error, 
    isLive, 
    lastUpdated 
  } = useLiveMarketData({ useWebSocket: true });

  // Toggle favorite
  const toggleFavorite = (coinId) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(coinId)
        ? prev.filter(id => id !== coinId)
        : [...prev, coinId];
      localStorage.setItem('marketFavorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  // Format global stats for display
  const statsConfig = [
    { key: 'marketCap', icon: Globe },
    { key: 'volume24h', icon: Activity },
    { key: 'btcDominance', icon: BarChart3 },
    { key: 'fearGreed', icon: TrendingUp },
  ];

  // Filter and sort data
  const filteredData = useMemo(() => {
    let result = [...coins];

    // Apply search filter
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      result = result.filter(
        coin =>
          coin.name.toLowerCase().includes(query) ||
          coin.symbol.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (typeof aVal === 'string') {
        return aVal.localeCompare(bVal);
      }
      return (bVal || 0) - (aVal || 0); // Default to desc for numeric
    });

    return result;
  }, [coins, searchTerm, sortBy]);

  // Format time since last update
  const getTimeSinceUpdate = () => {
    if (!lastUpdated) return 'Never';
    const seconds = Math.floor((Date.now() - lastUpdated) / 1000);
    if (seconds < 5) return 'Just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  if (isLoading) {
    return (
      <div className="market-page">
        <div className="market-bg">
          <div className="floating-coin coin-1">₿</div>
          <div className="floating-coin coin-2">Ξ</div>
          <div className="floating-coin coin-3">S</div>
        </div>
        <div className="market-container">
          <div className="market-loading">
            <div className="loading-spinner-large" />
            <p>Loading market data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="market-page">
        <div className="market-container">
          <div className="market-error">
            <p>Error loading market data: {error}</p>
            <button onClick={() => window.location.reload()} className="retry-btn">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="market-page">
      {/* Animated Background */}
      <div className="market-bg">
        <div className="floating-coin coin-1">₿</div>
        <div className="floating-coin coin-2">Ξ</div>
        <div className="floating-coin coin-3">S</div>
        <div className="glow-orb orb-1"></div>
        <div className="glow-orb orb-2"></div>
      </div>

      <div className="market-container">
        {/* Hero Section */}
        <section className="market-hero">
          <h1 className="market-title">
            <span className="gradient-text">Global</span> Crypto Market
          </h1>
          <p className="market-subtitle">
            Real-time cryptocurrency prices, market cap, and trading volume across top assets
          </p>
          {isLive && (
            <div className="live-indicator">
              <span className="live-dot" />
              <span className="live-text">Live Updates</span>
              <span className="update-time">Updated {getTimeSinceUpdate()}</span>
            </div>
          )}
        </section>

        {/* Global Stats */}
        <section className="global-stats">
          {statsConfig.map((stat, index) => {
            const statData = globalStats[stat.key];
            if (!statData) return null;
            
            return (
              <div 
                key={stat.key} 
                className="stat-card" 
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="stat-icon">
                  <stat.icon size={24} />
                </div>
                <div className="stat-info">
                  <span className="stat-label">{statData.label}</span>
                  <span className="stat-value">{statData.value}</span>
                  <span className={`stat-change ${statData.change?.isPositive ? 'positive' : statData.change?.isPositive === false ? 'negative' : 'neutral'}`}>
                    {statData.change?.value}
                  </span>
                </div>
              </div>
            );
          })}
        </section>

        {/* Market Table Section */}
        <section className="market-table-section">
          {/* Controls */}
          <div className="table-controls">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search coins..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="control-filters">
              <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                <option value="1h">1H</option>
                <option value="24h">24H</option>
                <option value="7d">7D</option>
                <option value="30d">30D</option>
              </select>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="marketCapRank">Rank</option>
                <option value="marketCap">Market Cap</option>
                <option value="volume24h">Volume</option>
                <option value="change24h">Change</option>
                <option value="price">Price</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="market-table-wrapper">
            <table className="market-table">
              <thead>
                <tr>
                  <th className="col-fav"></th>
                  <th className="col-rank">#</th>
                  <th className="col-name">Name</th>
                  <th className="col-price">Price</th>
                  <th className="col-change">{timeframe} %</th>
                  <th className="col-chart">Chart</th>
                  <th className="col-volume">Volume</th>
                  <th className="col-mcap">Market Cap</th>
                  <th className="col-action">Trade</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((coin) => (
                  <tr key={coin.id} className="coin-row">
                    <td className="col-fav">
                      <button 
                        className={`fav-btn ${favorites.includes(coin.id) ? 'active' : ''}`}
                        onClick={() => toggleFavorite(coin.id)}
                      >
                        <Star size={16} fill={favorites.includes(coin.id) ? 'currentColor' : 'none'} />
                      </button>
                    </td>
                    <td className="col-rank">{coin.marketCapRank}</td>
                    <td className="col-name">
                      <div className="coin-info">
                        {coin.image ? (
                          <CurrencyIcon 
                            currency={{ id: coin.id, ticker: coin.symbol.toLowerCase(), name: coin.name }}
                            externalImage={coin.image}
                            size="md"
                          />
                        ) : (
                          <span className="coin-icon" style={{ backgroundColor: coin.color }}>
                            {coin.icon}
                          </span>
                        )}
                        <div className="coin-details">
                          <span className="coin-name">{coin.name}</span>
                          <span className="coin-symbol">{coin.symbol}</span>
                        </div>
                      </div>
                    </td>
                    <td className="col-price">{coin.formattedPrice}</td>
                    <td className={`col-change ${coin.change24h >= 0 ? 'positive' : 'negative'}`}>
                      <span className="change-badge">
                        {coin.change24h >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                        {Math.abs(coin.change24h).toFixed(2)}%
                      </span>
                    </td>
                    <td className="col-chart">
                      <div className={`spark-line ${coin.change24h >= 0 ? 'up' : 'down'}`}>
                        <svg viewBox="0 0 100 30" preserveAspectRatio="none">
                          <path
                            d={coin.sparklinePath}
                            fill="none"
                            strokeWidth="2"
                          />
                        </svg>
                      </div>
                    </td>
                    <td className="col-volume">{coin.formattedVolume}</td>
                    <td className="col-mcap">{coin.formattedMarketCap}</td>
                    <td className="col-action">
                      <a href="/#exchange" className="trade-btn">
                        Buy
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredData.length === 0 && (
            <div className="no-results">
              <p>No coins found matching &quot;{searchTerm}&quot;</p>
            </div>
          )}
        </section>

        {/* Trending Section */}
        <section className="trending-section">
          <h2>
            <Zap className="section-icon" />
            Trending Coins
          </h2>
          <div className="trending-grid">
            {trendingCoins.slice(0, 4).map((coin, index) => (
              <div 
                key={coin.id} 
                className="trending-card" 
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="trending-header">
                  {coin.image ? (
                    <CurrencyIcon 
                      currency={{ id: coin.id, ticker: coin.symbol.toLowerCase(), name: coin.name }}
                      externalImage={coin.image}
                      size="sm"
                    />
                  ) : (
                    <span className="trending-icon" style={{ backgroundColor: coin.color }}>
                      {coin.icon}
                    </span>
                  )}
                  <span className={`trending-change ${coin.change24h >= 0 ? 'positive' : 'negative'}`}>
                    {coin.change24h >= 0 ? '+' : ''}{coin.change24h.toFixed(2)}%
                  </span>
                </div>
                <div className="trending-info">
                  <span className="trending-name">{coin.name}</span>
                  <span className="trending-symbol">{coin.symbol}</span>
                </div>
                <span className="trending-price">{coin.formattedPrice}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default MarketPage;
